import React, { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch } from "store/store";
import { NotificationType } from "store/notification/types";
import { addNotification } from "store/notification/slice";
import { useTranslation } from "react-i18next";
import styles from "./index.module.scss";
import { useCallback } from "react";
import { useClaimCheck } from "hooks/useClaimCheck";
import { useRedirectIfDisallowed } from "hooks/useRedirectIfDisallowed";
import { Button } from "components/buttons/Button";
import { useCatalogAccessBanner } from "./hooks/useCatalogAccessBanner";
import { useSwitcherClient } from "hooks/useSwitcherClient";
import { RootState } from "store/reducers";
import { CreateOrUpdateSwitcherPlayerModal } from "components/modal/CreateOrUpdateSwitcherPlayerModal";
import { EditCatalogModal } from "components/modal/EditCatalogModal";
import { DragDropContext, Draggable, Droppable } from "@hello-pangea/dnd";
import type { DropResult } from "@hello-pangea/dnd";
import { PlayerDetails } from "components/entity-details/PlayerDetails";
import { useNavigate } from "react-router-dom";
import {
    DeviceType,
    PlayerDetailsResponse,
    VideoPlayerAspectRatio,
    VideoPlayerDefaultInteractiveTab,
    VideoPlayerEmbeddedDisplay,
    VideoPlayerIdleState,
    VideoPlayersResponse
} from "@switcherstudio/switcher-api-client";
import { AttentionModal } from "components/modal/AttentionModal";
import rollbar from "helpers/rollbar";
import { useSettableMemo } from "hooks/useSettableMemo";
import { Spinner } from "components/spinners/Spinner";
import { usePageHeader } from "hooks/usePageHeader";
import { openExternalUrlInNewTab } from "helpers/navigation";
import { useIsMobile } from "hooks/useIsMobile";
import { useIsTablet } from "hooks/useIsTablet";
import { CatalogLinkList } from "./CatalogLinkList";
import { Tabs } from "components/tabs/Tabs";
import { useCatalogData } from "hooks/useCatalogData";
import { CopyEmbedCode } from "views/page-content/CatalogPage/CopyEmbedCode/CopyEmbedCode";
import { ShareLink } from "./ShareLink/ShareLink";
import { Link } from "components/buttons/Link";
import { InlineButton } from "components/buttons/InlineButton";
import { closeCurrentModal, setActiveModal } from "store/modal/slice";
import { Modals } from "store/modal/types";

enum CatalogTabs {
    Collections = "collections",
    Settings = "settings",
    Share = "share"
}

export const CatalogPage: React.FC = () => {
    useCatalogAccessBanner();
    const { userInfo } = useSelector((state: RootState) => state.user);
    const { t } = useTranslation("catalog-page");
    const videoPlayerDisabled = useClaimCheck("videoplayer.disabled");
    useRedirectIfDisallowed(() => !videoPlayerDisabled, "/switcher-players");
    const [createModalOpen, setCreateModalOpen] = useState<boolean>(false);
    const [editCatalogModalOpen, setEditCatalogModalOpen] =
        useState<boolean>(false);
    const [selectedPlayerId, setSelectedPlayerId] = useState<string>();
    const dispatch = useDispatch<AppDispatch>();
    const hasVideoPlayerAccess = useClaimCheck("videoplayer");
    const { isMobile } = useIsMobile();
    const { isTablet } = useIsTablet();

    const [activeTab, setActiveTab] = useState<CatalogTabs>(
        CatalogTabs.Collections
    );

    const { catalogData, shareLink, catalogUpdateCustomSlug, fetchCatalog } =
        useCatalogData({
            projectId: userInfo?.MetaData?.Details?.ProjectId
        });

    const { data: videoLibraryUsage } = useSwitcherClient(
        (client) => client.cloudRecordings_GetUsage,
        {
            requestImmediately: true
        }
    );

    const {
        data: response,
        dispatchApiRequest: refetchPlayers,
        loading: refetchLoading
    } = useSwitcherClient(
        (client) => client.videoPlayersV2_GetVideoPlayersByProjectId,
        {
            requestImmediately: true,
            args: [userInfo?.MetaData?.Details?.ProjectId]
        }
    );

    const navigate = useNavigate();

    const { dispatchApiRequest: deletePlayer } = useSwitcherClient(
        (client) => client.projectsVideoPlayer_DeleteVideoPlayer
    );

    const [playersResponse, setPlayersResponse] = useSettableMemo(
        () => response,
        [response]
    );

    const userHasCreatedCollections = useMemo(
        () =>
            !playersResponse?.Players?.every(
                (response) => response?.Details?.IsLibraryPlayer
            ),
        [playersResponse]
    );

    const { dispatchApiRequest: updatePlayer, loading: loadingUpdate } =
        useSwitcherClient(
            (client) => client.projectsVideoPlayer_PutVideoPlayer,
            {
                requestImmediately: false,
                onError: () => refetchPlayers()
            }
        );

    const { dispatchApiRequest: sortVideoPlayer, loading: loadingSort } =
        useSwitcherClient((client) => client.videoPlayersV2_SortVideoPlayer, {
            requestImmediately: false,
            onSuccess: (response) => {
                setPlayersResponse(response);
            },
            onError: (e) => {
                rollbar.error("Error reordering collection", e);
                dispatch(
                    addNotification({
                        type: NotificationType.Danger,
                        message: t("messages:failed-to-reorder-collections")
                    })
                );
                refetchPlayers();
            }
        });

    const catalogName = useMemo(() => {
        return (
            catalogData?.Details?.Title ??
            t("customization-page:default-catalog")
        );
    }, [catalogData?.Details?.Title, t]);

    const catalogDescription = useMemo(() => {
        return (
            catalogData?.Details?.Description ??
            t("customization-page:default-description")
        );
    }, [catalogData?.Details?.Description, t]);

    usePageHeader({
        autoSave: loadingUpdate || loadingSort,
        showBreadcrumbs: false,
        subTitle: t("catalog-page:upcoming-content-header")
    });

    // clear switcher player app from embed.js
    useEffect(() => {
        return () => {
            window.switcherPlayerApp = undefined;
        };
    }, []);

    const closeAttentionModal = useCallback(() => {
        dispatch(closeCurrentModal());
    }, [dispatch]);

    const handleDeleteConfirm = useCallback(() => {
        deletePlayer([selectedPlayerId, userInfo?.MetaData?.Details?.ProjectId])
            .then(() => {
                dispatch(
                    addNotification({
                        type: NotificationType.Success,
                        message: t("collections:messages:delete-success")
                    })
                );
                refetchPlayers();
            })
            .catch(() => {
                dispatch(
                    addNotification({
                        type: NotificationType.Danger,
                        message: t("collections:errors:delete-error")
                    })
                );
            })
            .finally(() => closeAttentionModal());
    }, [
        deletePlayer,
        selectedPlayerId,
        userInfo?.MetaData?.Details?.ProjectId,
        dispatch,
        t,
        refetchPlayers,
        closeAttentionModal
    ]);

    const handleDragEnd = useCallback(
        async (result: DropResult) => {
            if (!result?.destination) return;
            if (result.reason === "DROP") {
                const collections = [...playersResponse?.Players];

                const targetPlayerIndex = collections.findIndex(
                    (p) => p?.Details?.Id === result?.draggableId
                );

                if (targetPlayerIndex === result?.destination.index) return;

                const targetPlayer = collections[targetPlayerIndex];

                collections.splice(result.source.index, 1);
                collections.splice(result.destination.index, 0, targetPlayer);

                setPlayersResponse({
                    Players: collections
                });

                const destinationPreviousRank =
                    collections[result.destination.index - 1]?.Details
                        ?.OrdinalRank?.Rank;
                const destinationNextRank =
                    collections[result.destination.index + 1]?.Details
                        ?.OrdinalRank?.Rank;

                await sortVideoPlayer([
                    {
                        VideoPlayer: {
                            ...targetPlayer?.Details,
                            AspectRatio: targetPlayer?.Details
                                ?.AspectRatio as unknown as VideoPlayerAspectRatio,
                            OrdinalRank: targetPlayer?.Details?.OrdinalRank,
                            EmbeddedDisplay: targetPlayer?.Details
                                ?.EmbeddedDisplay as unknown as VideoPlayerEmbeddedDisplay,
                            DefaultInteractiveTab: targetPlayer?.Details
                                ?.DefaultInteractiveTab as unknown as VideoPlayerDefaultInteractiveTab,
                            IdleState: targetPlayer?.Details
                                ?.IdleState as unknown as VideoPlayerIdleState
                        },
                        PreviousOrdinal: destinationPreviousRank,
                        NextOrdinal: destinationNextRank
                    }
                ]);
            }
        },
        [setPlayersResponse, sortVideoPlayer, playersResponse]
    );

    const onPrivacyToggle = useCallback(
        async (player: PlayerDetailsResponse, value: boolean) => {
            await updatePlayer([
                player.Id,
                {
                    VideoPlayer: {
                        ...player,
                        ShowInCatalog: value,
                        AspectRatio:
                            player?.AspectRatio as unknown as VideoPlayerAspectRatio,
                        OrdinalRank: player?.OrdinalRank,
                        EmbeddedDisplay:
                            player?.EmbeddedDisplay as unknown as VideoPlayerEmbeddedDisplay,
                        DefaultInteractiveTab:
                            player?.DefaultInteractiveTab as unknown as VideoPlayerDefaultInteractiveTab,
                        IdleState:
                            player?.IdleState as unknown as VideoPlayerIdleState
                    }
                },
                userInfo?.MetaData?.Details?.ProjectId
            ]);

            await refetchPlayers();
        },
        [refetchPlayers, updatePlayer, userInfo?.MetaData?.Details?.ProjectId]
    );

    const handlePreviewClick = useCallback(
        (type: "desktop" | "tablet" | "mobile") => {
            switch (type) {
                case "desktop":
                    openExternalUrlInNewTab(`${shareLink}`);
                    break;
                case "tablet":
                    if (isTablet) {
                        openExternalUrlInNewTab(`${shareLink}`);
                    } else {
                        openExternalUrlInNewTab(
                            `device-preview?device=${DeviceType.IPad}&catalogId=${catalogData?.Details?.Id}`
                        );
                    }
                    break;
                case "mobile":
                    if (isMobile) {
                        openExternalUrlInNewTab(`${shareLink}`);
                    } else {
                        openExternalUrlInNewTab(
                            `device-preview?device=${DeviceType.IPhone}&catalogId=${catalogData?.Details?.Id}`
                        );
                    }
                    break;
                default:
                    openExternalUrlInNewTab(`${shareLink}`);
            }
        },
        [shareLink, isTablet, isMobile, catalogData?.Details?.Id]
    );

    const createCollectionButton = useMemo(() => {
        return (
            hasVideoPlayerAccess &&
            !videoPlayerDisabled && (
                <>
                    <div
                        className={`${styles["collection-button"]} ${styles["header-button"]}`}
                    >
                        <Button
                            type="primary"
                            onClick={() => setCreateModalOpen(true)}
                        >
                            {userHasCreatedCollections
                                ? t("collections-page:create-collection")
                                : t("collections-page:create-first-collection")}
                        </Button>
                    </div>
                    <CreateOrUpdateSwitcherPlayerModal
                        isOpen={createModalOpen}
                        setIsOpen={setCreateModalOpen}
                        type="create"
                        onSubmit={(vpr: VideoPlayersResponse) => {
                            navigate(
                                `/catalog/collections/${vpr?.VideoPlayers?.[0]?.Id}`
                            );
                        }}
                    />
                </>
            )
        );
    }, [
        createModalOpen,
        userHasCreatedCollections,
        hasVideoPlayerAccess,
        t,
        videoPlayerDisabled,
        navigate
    ]);

    const openAttentionModal = useCallback(
        () =>
            dispatch(
                setActiveModal({
                    id: Modals.AttentionModal,
                    type: Modals.AttentionModal,
                    component: (
                        <AttentionModal
                            isOpen={true}
                            setIsOpen={closeAttentionModal}
                            handleCancel={closeAttentionModal}
                            hasContinueButton={true}
                            handleContinue={handleDeleteConfirm}
                            customHeader={t(
                                "collections:messages:delete-collection-title"
                            )}
                            continueText={t("buttons:delete")}
                        >
                            <div>
                                {playersResponse?.Players?.find(
                                    (pr) =>
                                        pr?.Details?.Id === selectedPlayerId &&
                                        // if the video player OR any of the broadcasts in the player have entitlements
                                        (pr?.Entitlements?.ProductEntitlements
                                            ?.length > 0 ||
                                            pr.Playlists?.[0]?.Items?.some(
                                                (bc) =>
                                                    bc.Entitlements
                                                        ?.ProductEntitlements
                                                        ?.length > 0
                                            ))
                                )
                                    ? t(
                                          "collections:messages:delete-collection-gated"
                                      )
                                    : t(
                                          "collections:messages:delete-collection"
                                      )}
                            </div>
                        </AttentionModal>
                    )
                })
            ),
        [
            closeAttentionModal,
            dispatch,
            handleDeleteConfirm,
            playersResponse?.Players,
            selectedPlayerId,
            t
        ]
    );

    const collections = useMemo(() => {
        const hasVideos = videoLibraryUsage?.TotalRecordings > 0;

        return (
            <>
                <div
                    className={`${styles["collections-container"]} ${
                        videoPlayerDisabled
                            ? styles["collections-disabled"]
                            : ""
                    } ${refetchLoading ? styles["reloading-inactive"] : ""}`}
                >
                    {!hasVideos && (
                        <p>
                            {t("catalog-page:no-collections")}{" "}
                            <Link to="/video-library">
                                {t("catalog-page:upload-prompt")}
                            </Link>
                        </p>
                    )}
                    <DragDropContext onDragEnd={handleDragEnd}>
                        <Droppable droppableId="collections">
                            {(droppableProvided, snapshot) => (
                                <div
                                    ref={droppableProvided.innerRef}
                                    {...droppableProvided.droppableProps}
                                >
                                    {playersResponse?.Players?.map(
                                        (pr, index) => (
                                            <Draggable
                                                draggableId={
                                                    pr?.Details?.Id ??
                                                    (index + 1).toString()
                                                }
                                                key={
                                                    pr?.Details?.Id ??
                                                    (index + 1).toString()
                                                }
                                                index={index}
                                            >
                                                {(draggableProvided) => (
                                                    <div
                                                        className={`${styles["collection-item"]}`}
                                                        {...draggableProvided.draggableProps}
                                                        {...draggableProvided.dragHandleProps}
                                                        ref={
                                                            draggableProvided.innerRef
                                                        }
                                                    >
                                                        <PlayerDetails
                                                            item={pr}
                                                            key={
                                                                pr?.Details?.Id
                                                            }
                                                            showEdit={
                                                                !pr?.Details
                                                                    ?.IsLibraryPlayer &&
                                                                !pr?.Details
                                                                    ?.IsLivestreamPlayer
                                                            }
                                                            showDelete={
                                                                !pr?.Details
                                                                    ?.IsLibraryPlayer &&
                                                                !pr?.Details
                                                                    ?.IsLivestreamPlayer
                                                            }
                                                            showShare={true}
                                                            showPrivacyToggle={
                                                                true
                                                            }
                                                            isDraggable={true}
                                                            onPrivacyToggle={
                                                                onPrivacyToggle
                                                            }
                                                            handleDelete={(
                                                                videoPlayerId
                                                            ) => {
                                                                setSelectedPlayerId(
                                                                    videoPlayerId
                                                                );
                                                                openAttentionModal();
                                                            }}
                                                            hasToggleRow={true}
                                                        />
                                                    </div>
                                                )}
                                            </Draggable>
                                        )
                                    )}
                                    <div
                                        className={
                                            snapshot.isDraggingOver
                                                ? styles["placeholder-div"]
                                                : ""
                                        }
                                    >
                                        {droppableProvided.placeholder}
                                    </div>
                                </div>
                            )}
                        </Droppable>
                    </DragDropContext>
                </div>
            </>
        );
    }, [
        handleDragEnd,
        onPrivacyToggle,
        t,
        videoLibraryUsage,
        videoPlayerDisabled,
        playersResponse,
        refetchLoading,
        openAttentionModal
    ]);

    const handleUpdateCustomSlug = useCallback(
        (customSlug: string) => {
            catalogUpdateCustomSlug([catalogData?.Details?.Id, customSlug]);
        },
        [catalogData?.Details?.Id, catalogUpdateCustomSlug]
    );

    const share = useMemo(() => {
        return (
            <>
                <CopyEmbedCode
                    entityId={catalogData?.Details?.Id}
                    variant="catalog"
                />
                <ShareLink
                    currentSlug={catalogData?.Details?.CustomSlug}
                    shareLink={shareLink}
                    variant="catalog"
                    updateCustomSlug={handleUpdateCustomSlug}
                />
            </>
        );
    }, [
        catalogData?.Details?.Id,
        shareLink,
        handleUpdateCustomSlug,
        catalogData?.Details?.CustomSlug
    ]);

    const settings = useMemo(() => {
        return (
            <div className={styles["link-list"]}>
                <CatalogLinkList
                    hasVideoPlayerAccess={hasVideoPlayerAccess}
                    t={t}
                    handlePreview={handlePreviewClick}
                />
            </div>
        );
    }, [handlePreviewClick, hasVideoPlayerAccess, t]);

    return (
        <>
            {!playersResponse || !catalogData ? (
                <div className={styles["loading"]}>
                    <Spinner size={128} />
                </div>
            ) : (
                <div className="row">
                    <div className="col-md-8">
                        <p className={styles["edit-details"]}>
                            <p className={styles["catalog-details"]}>
                                <strong>{catalogName}</strong>

                                <span>{catalogDescription}</span>

                                <InlineButton
                                    variant="tertiary"
                                    onClick={() =>
                                        setEditCatalogModalOpen(true)
                                    }
                                >
                                    {t("catalog-page:edit-catalog-details")}
                                </InlineButton>
                                <EditCatalogModal
                                    isOpen={editCatalogModalOpen}
                                    setIsOpen={setEditCatalogModalOpen}
                                    onSubmit={() => fetchCatalog()}
                                />
                            </p>
                        </p>
                        <div className={styles["tab-view"]}>
                            {isMobile && (
                                <Tabs<CatalogTabs>
                                    tabs={[
                                        {
                                            id: CatalogTabs.Collections,
                                            label: t(
                                                "catalog-page:Collections"
                                            ),
                                            component: (
                                                <>
                                                    {createCollectionButton}
                                                    {collections}
                                                </>
                                            )
                                        },
                                        {
                                            id: CatalogTabs.Settings,
                                            label: t("catalog-page:settings"),
                                            component: settings
                                        },
                                        {
                                            id: CatalogTabs.Share,
                                            label: t("catalog-page:share"),
                                            component: share
                                        }
                                    ]}
                                    activeTab={activeTab}
                                    setActiveTab={setActiveTab}
                                />
                            )}
                        </div>
                        {!isMobile && (
                            <>
                                {createCollectionButton}
                                {collections}
                            </>
                        )}
                    </div>
                    <div className="col-md-4">
                        {!isMobile && settings}

                        {!isMobile && share}
                    </div>
                </div>
            )}
        </>
    );
};
